import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sociedad, IAccionFormativa, ICursoAccionFormativa, ICurso, IPoliza } from '../types';
import FormHeader from '../components/FormHeader';
import SnackBar from '../components/SnackBar';
import Modal from '../components/Modal';
import { pdfService } from '../services/pdfService';
import { sociedadService } from '../services/sociedadService';
import { sociedadStorage } from '../stores/sociedadStorage';
import './summary-form.css';

interface SummaryFormProps {
  sociedad: Sociedad;
  accionFormativa?: IAccionFormativa;
  cursosAccionFormativa?: ICursoAccionFormativa[];
  curso?: ICurso;
  poliza?: IPoliza;
  onBack: () => void;
  onNewRequest: () => void;
  onNavigateToStart?: () => void;
}

/**
 * @fileoverview Componente de resumen final para mostrar todos los datos
 * @description Muestra un resumen completo de todos los datos que ya han sido guardados en la base de datos.
 * Permite generar un PDF con el resumen o crear una nueva solicitud.
 * Los datos NO se envían desde aquí ya que se guardaron progresivamente durante el proceso.
 */

const SummaryForm: React.FC<SummaryFormProps> = ({
  sociedad,
  accionFormativa,
  cursosAccionFormativa,
  curso,
  poliza,
  onBack,
  onNewRequest,
  onNavigateToStart
}) => {
  const navigate = useNavigate();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // useRef para evitar múltiples ejecuciones del useEffect
  const pdfAutoGeneratedRef = useRef(false);
  const isGeneratingRef = useRef(false);

  /**
   * Valida que haya datos válidos antes de renderizar
   * Si no hay sociedad, redirige al inicio
   * Si hay ambos tipos de formación, redirige al inicio (datos inconsistentes)
   */
  useEffect(() => {
    if (!sociedad) {
      console.warn('[SummaryForm] No hay datos de sociedad, redirigiendo al inicio');
      navigate('/', { replace: true });
      return;
    }

    // Validar que solo haya un tipo de formación (acción formativa O curso, no ambos)
    const hasAccionFormativa = accionFormativa !== undefined && accionFormativa !== null;
    const hasCurso = curso !== undefined && curso !== null;

    if (hasAccionFormativa && hasCurso) {
      console.error('[SummaryForm] ERROR: Se detectaron ambos tipos de formación. Esto indica datos inconsistentes. Redirigiendo al inicio para limpiar.');
      // Si hay ambos tipos de formación, los datos están inconsistentes
      // Redirigir al inicio para que el usuario inicie una nueva solicitud limpia
      navigate('/', { replace: true });
      return;
    }

  }, [sociedad, accionFormativa, curso, poliza, navigate]);

  /**
   * Genera automáticamente el PDF al cargar el componente
   * @description Se ejecuta una sola vez al montar el componente
   */
  useEffect(() => {
    const generarPDFAutomatico = async () => {
      // Evitar múltiples ejecuciones simultáneas o repetidas
      if (pdfAutoGeneratedRef.current || isGeneratingRef.current) {
        return;
      }

      isGeneratingRef.current = true;

      try {
        // Validar que haya sociedad antes de generar PDF
        if (!sociedad) {
          console.warn('[SummaryForm] No hay datos de sociedad para generar PDF');
          return;
        }

        // Obtener la sociedad actualizada del LocalStorage (que incluye idSociedad)
        const storedSociedad = sociedadStorage.getSociedad();
        const sociedadToUse = storedSociedad || sociedad;

        // Asegurar que solo se incluyan datos de un tipo de formación
        // Si hay acción formativa, no incluir curso y viceversa
        const data = {
          sociedad: sociedadToUse,
          accionFormativa: accionFormativa || undefined,
          cursosAccionFormativa: accionFormativa ? (cursosAccionFormativa || undefined) : undefined,
          curso: accionFormativa ? undefined : (curso || undefined),
          poliza: poliza || undefined
        };

        // Generar PDF
        const pdf = await pdfService.generarPDFResumen(data);

        // Obtener el ID de sociedad del LocalStorage (más confiable)
        const storedSociedadId = sociedadStorage.getSociedadId();
        const sociedadIdRaw = storedSociedadId ?? sociedad.idSociedad ?? 0;
        const sociedadId = Number(sociedadIdRaw) || 0;

        if (!sociedadId || sociedadId <= 0) {
          console.warn('⚠️ No hay idSociedad válido para subir el PDF. Se omite la subida automática.');
        } else {
          // Solo subir a la API, no descargar localmente (el servidor genera el filename)
          await pdfService.subirPDF(pdf, sociedadId);
        }

        pdfAutoGeneratedRef.current = true;
      } catch (error) {
        // No mostrar error al usuario para la generación automática
      } finally {
        isGeneratingRef.current = false;
      }
    };

    generarPDFAutomatico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Array vacío para ejecutar solo una vez al montar

  /**
   * Formatea una fecha para mostrar
   * @param date - Fecha a formatear
   * @returns Fecha formateada como string
   */
  const formatDate = (date: Date | null): string => {
    if (!date) return 'No especificado';
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  /**
   * Obtiene el nombre de la asociación basado en el ID
   * @param id - ID de la asociación
   * @returns Nombre de la asociación
   */
  const getAsociacionName = (id: string): string => {
    const asociaciones: { [key: string]: string } = {
      'AEGEF': 'ASOCIACION ESTATAL DE GRANDES EMPRESAS DE FORMACION',
      'CECAP-EXT': 'CECAP EXTREMADURA',
      'CECAP-CYL': 'CECAP CASTILLA Y LEON',
      'CECAP-AND': 'CECAP ANDALUCIA',
      'CECAP-VAL': 'CECAP COMUNIDAD VALENCIANA',
      'SIN-CECAP': 'SIN ASOCIACION',
      'FMASTER': 'FORMASTER'
    };
    return asociaciones[id] || id;
  };

  /**
   * Sanitiza el nombre del archivo para evitar caracteres problemáticos
   * @param razonSocial - Razón social de la sociedad
   * @returns Nombre de archivo sanitizado
   */
  const sanitizeFilename = (razonSocial: string): string => {
    return razonSocial
      .replace(/[^a-zA-Z0-9\s\-_]/g, '') // Eliminar caracteres especiales excepto espacios, guiones y guiones bajos
      .replace(/\s+/g, '-') // Reemplazar espacios con guiones
      .substring(0, 50); // Limitar longitud
  };

  /**
   * Genera y descarga el PDF localmente (acción manual del usuario)
   * @description Solo descarga el PDF, no lo sube a la API (ya se subió automáticamente)
   */
  const handleGeneratePDF = async () => {
    if (!sociedad) {
      setErrorMessage('No hay datos de solicitud disponibles para generar el PDF.');
      setShowErrorModal(true);
      return;
    }

    setIsGeneratingPDF(true);

    try {
      // Asegurar que solo se incluyan datos de un tipo de formación
      const data = {
        sociedad,
        accionFormativa: accionFormativa || undefined,
        cursosAccionFormativa: accionFormativa ? (cursosAccionFormativa || undefined) : undefined,
        curso: accionFormativa ? undefined : (curso || undefined),
        poliza: poliza || undefined
      };

      // Solo generar y descargar PDF localmente
      const pdf = await pdfService.generarPDFResumen(data);
      const filename = `resumen-solicitud-${sanitizeFilename(data.sociedad.razonSocial)}.pdf`;

      // Solo descargar, no subir (ya se subió automáticamente)
      pdfService.descargarPDF(pdf, filename);

    } catch (error) {
      setErrorMessage('Error al generar el PDF. Por favor, intente nuevamente.');
      setShowErrorModal(true);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Si no hay sociedad, no renderizar nada (el useEffect redirigirá)
  if (!sociedad) {
    return null;
  }

  // Determinar qué tipo de formación mostrar (solo uno, no ambos)
  // Si hay acción formativa, no mostrar curso y viceversa
  const shouldShowAccionFormativa = accionFormativa !== undefined && accionFormativa !== null;
  const shouldShowCurso = !shouldShowAccionFormativa && curso !== undefined && curso !== null;

  return (
    <div className="container">
      <div className="form-container">
        <FormHeader onLogoClick={onNewRequest} />

        <div className="summary-notice">
          <p style={{ textAlign: 'center', fontSize: '20px' }}><strong>SOLICITUD ENVIADA</strong></p><br />

          <p><strong>En la parte inferior puede generar un PDF</strong> con el resumen de la información.<br /><br />
            <strong>Cualquier consulta o modificación</strong> de esta solicitud envie un correo a <strong><a href="mailto:cursosformacion@cgseguros.es">cursosformacion@cgseguros.es</a> adjuntando el PDF generado.</strong></p>

        </div>

        <h2 className="form-subtitle-summary">RESUMEN</h2>
        <div className="summary-content">
          {/* Datos de la Sociedad */}
          <div className="summary-section">
            <h3 className="summary-title">Datos del Solicitante</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <strong>Razón Social:</strong>
                <span>{sociedad.razonSocial}</span>
              </div>
              <div className="summary-item">
                <strong>CIF/NIF:</strong>
                <span>{sociedad.cif}</span>
              </div>
              <div className="summary-item">
                <strong>Dirección:</strong>
                <span>{sociedad.direccion}</span>
              </div>
              <div className="summary-item">
                <strong>Localidad:</strong>
                <span>{sociedad.localidad}</span>
              </div>
              <div className="summary-item">
                <strong>Provincia:</strong>
                <span>{sociedad.provincia}</span>
              </div>
              <div className="summary-item">
                <strong>Código Postal:</strong>
                <span>{sociedad.codigoPostal}</span>
              </div>
              <div className="summary-item">
                <strong>Persona de Contacto:</strong>
                <span>{sociedad.personaContacto}</span>
              </div>
              <div className="summary-item">
                <strong>Teléfono:</strong>
                <span>{sociedad.telefono}</span>
              </div>
              <div className="summary-item">
                <strong>Email:</strong>
                <span>{sociedad.email}</span>
              </div>
              <div className="summary-item">
                <strong>Asociación:</strong>
                <span>{getAsociacionName(sociedad.condicionesIdAsociacion)}</span>
              </div>
              <div className="summary-item">
                <strong>Archivo de Resolución:</strong>
                <span>{sociedad.fileResolucion || 'No proporcionado'}</span>
              </div>
              <div className="summary-item">
                <strong>Archivo de Texto de Seguros:</strong>
                <span>{sociedad.fileTextoSeguros || 'No proporcionado'}</span>
              </div>
            </div>
          </div>

          {/* Datos de Acción Formativa */}
          {shouldShowAccionFormativa && accionFormativa && (
            <div className="summary-section">
              <h3 className="summary-title">Convocatoria / Varios cursos</h3>
              <div className="summary-grid">
                <div className="summary-item">
                  <strong>Nombre:</strong>
                  <span>{accionFormativa.nombre ?? 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <strong>Expediente:</strong>
                  <span>{accionFormativa.expediente}</span>
                </div>
                <div className="summary-item">
                  <strong>Administración Pública:</strong>
                  <span>{accionFormativa.administracionPublica ?? 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <strong>Número de Alumnos:</strong>
                  <span>{accionFormativa.numAlumnos}</span>
                </div>
                <div className="summary-item">
                  <strong>Número de Horas:</strong>
                  <span>{accionFormativa.numHoras ?? 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <strong>Fecha de Inicio:</strong>
                  <span>{formatDate(accionFormativa.fechaInicio)}</span>
                </div>
                <div className="summary-item">
                  <strong>Fecha de Fin:</strong>
                  <span>{formatDate(accionFormativa.fechaFin)}</span>
                </div>
                <div className="summary-item">
                  <strong>Comunidad Autónoma:</strong>
                  <span>{accionFormativa.ccaa}</span>
                </div>
                <div className="summary-item">
                  <strong>Ubicación de los cursos:</strong>
                  <span>{accionFormativa.ubicacion ?? 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <strong>Prácticas:</strong>
                  <span>{accionFormativa.practicas ?? 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <strong>Modalidad:</strong>
                  <span>{accionFormativa.modalidad ?? 'No especificado'}</span>
                </div>
                {accionFormativa.cursosText &&
                  accionFormativa.cursosText.trim() !== '' &&
                  accionFormativa.cursosText.trim() !== '<p><br></p>' &&
                  accionFormativa.cursosText.trim() !== '<p><br /></p>' && (
                    <div className="summary-item" style={{ gridColumn: '1 / -1' }}>
                      <strong>Cursos:</strong>
                      <div
                        style={{
                          marginTop: '8px',
                          padding: '12px',
                          backgroundColor: '#f9f9f9',
                          borderRadius: '4px',
                          border: '1px solid #e0e0e0'
                        }}
                        dangerouslySetInnerHTML={{ __html: accionFormativa.cursosText }}
                      />
                    </div>
                  )}
              </div>

              {/* Cursos de la Convocatoria / Varios cursos */}
              {cursosAccionFormativa && cursosAccionFormativa.length > 0 && (
                <div className="summary-subsection">
                  <h4 className="summary-subtitle">Cursos Asociados</h4>
                  {cursosAccionFormativa.map((curso, index) => (
                    <div key={index} className="summary-course">
                      <div className="summary-item">
                        <strong>Un curso {index + 1}:</strong>
                        <span>{curso.nombre}</span>
                      </div>
                      <div className="summary-item">
                        <strong>Horas:</strong>
                        <span>{curso.horas ?? 'No especificado'}</span>
                      </div>
                      <div className="summary-item">
                        <strong>Alumnos:</strong>
                        <span>{curso.numAlumnos}</span>
                      </div>
                      <div className="summary-item">
                        <strong>Expediente:</strong>
                        <span>{curso.expediente || 'No especificado'}</span>
                      </div>
                      <div className="summary-item">
                        <strong>Fecha de Inicio:</strong>
                        <span>{formatDate(curso.fechaInicio)}</span>
                      </div>
                      <div className="summary-item">
                        <strong>Fecha de Fin:</strong>
                        <span>{formatDate(curso.fechaFin)}</span>
                      </div>
                      <div className="summary-item">
                        <strong>Ubicación:</strong>
                        <span>{curso.ubicacion || 'No especificado'}</span>
                      </div>
                      <div className="summary-item">
                        <strong>Modalidad:</strong>
                        <span>{curso.modalidad || 'No especificado'}</span>
                      </div>
                      <div className="summary-item">
                        <strong>Prácticas:</strong>
                        <span>{curso.practicas || 'No especificado'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Datos de Un curso */}
          {shouldShowCurso && curso && (
            <div className="summary-section">
              <h3 className="summary-title">Un curso</h3>
              <div className="summary-grid">
                <div className="summary-item">
                  <strong>Nombre:</strong>
                  <span>{curso.nombre}</span>
                </div>
                <div className="summary-item">
                  <strong>Expediente:</strong>
                  <span>{curso.expediente}</span>
                </div>
                <div className="summary-item">
                  <strong>Administración Pública:</strong>
                  <span>{curso.administracionPublica ?? 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <strong>Número de Alumnos:</strong>
                  <span>{curso.numAlumnos}</span>
                </div>
                <div className="summary-item">
                  <strong>Número de Horas:</strong>
                  <span>{curso.numHoras ?? 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <strong>Fecha de Inicio:</strong>
                  <span>{formatDate(curso.fechaInicio)}</span>
                </div>
                <div className="summary-item">
                  <strong>Fecha de Fin:</strong>
                  <span>{formatDate(curso.fechaFin)}</span>
                </div>
                <div className="summary-item">
                  <strong>Comunidad Autónoma:</strong>
                  <span>{curso.ccaa}</span>
                </div>
                <div className="summary-item">
                  <strong>Ubicación:</strong>
                  <span>{curso.ubicacionCurso ?? 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <strong>Prácticas:</strong>
                  <span>{curso.practicas ?? 'No especificado'}</span>
                </div>
                <div className="summary-item">
                  <strong>Modalidad:</strong>
                  <span>{curso.modalidad ?? 'No especificado'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Datos de la Póliza */}
          {poliza && (
            <div className="summary-section">
              <h3 className="summary-title">Póliza de Seguros</h3>
              <div className="summary-grid">
                <div className="summary-item">
                  <strong>Tipo de Póliza:</strong>
                  <span>{poliza.tipo}</span>
                </div>
                <div className="summary-item">
                  <strong>Forma de Pago:</strong>
                  <span>{poliza.formaPago}</span>
                </div>
                <div className="summary-item">
                  <strong>IBAN:</strong>
                  <span>{poliza.iban}</span>
                </div>
                <div className="summary-item">
                  <strong>Comentarios:</strong>
                  <span>{poliza.comentarios || 'Sin comentarios'}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="button-group">
          <button
            type="button"
            className="btn btn-success-pdf"
            onClick={handleGeneratePDF}
            disabled={isGeneratingPDF}
          >
            {isGeneratingPDF ? 'Generando PDF...' : 'Descargar PDF'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onNewRequest}
          >
            Nueva Solicitud
          </button>
        </div>

        {/* SnackBar para éxito */}
        <SnackBar
          isOpen={showSuccessSnackbar}
          onClose={() => setShowSuccessSnackbar(false)}
          message={successMessage}
          type="success"
          duration={5000}
          position="bottom-center"
        />

        {/* Modal para errores */}
        <Modal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title="Error al generar PDF"
        >
          <div style={{ color: '#d32f2f', lineHeight: '1.5' }}>
            <p style={{ margin: '0 0 16px 0' }}>
              <strong>Error:</strong> {errorMessage}
            </p>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>
              Por favor, intente nuevamente o contacte con soporte técnico.
            </p>
            <div style={{ textAlign: 'right' }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowErrorModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default SummaryForm;
